// MSAL popup redirect guard.
//
// MSAL uses the popup flow (acquireTokenPopup, response_mode=fragment): after
// authenticating, the popup lands on `<redirectUri>/#code=<authcode>&state=...`
// and the opener's MSAL reads that code by polling the popup's URL.
//
// Our hash-router (createWebHashHistory) would corrupt that fragment first:
//   1. creating the router (at import time) rewrites `#code=...` to `#/code=...`
//      — it forces a leading '/', which MSAL cannot parse; then
//   2. mounting the app matches `#/code=...` against the catch-all route and
//      navigates to `#/404` (Error404.vue), destroying the code.
// Both surface as a "404 in the popup" and a failed login.
//
// Guard: this module is imported FIRST in main.ts, so it snapshots the raw
// fragment before './router' is evaluated. main.ts calls handleMsalRedirectLanding()
// before bootstrapping; on a landing it restores the raw fragment and signals the
// caller to skip bootstrap, leaving the URL intact for the opener to read.

// Raw URL fragment captured before the router (imported later) can rewrite it.
const initialHash = typeof window !== 'undefined' ? window.location.hash : ''

// MSAL fragment responses carry the auth artifact as a param: code= (success) or
// error=, plus id_token/access_token for token responses. A leading '/' the
// hash-router may have injected is tolerated; app routes (`#/sign-in`, `#/404`)
// never carry these params.
const MSAL_RESPONSE_PARAM = /(^|&)(code|error|id_token|access_token)=/

/**
 * True when `hash` is an MSAL fragment response (`#code=...`, or the
 * router-normalized `#/code=...`) rather than an application route.
 */
export function isMsalRedirectHash(hash: string): boolean {
  // Strip the leading '#' and an optional '/' the hash-router may have added.
  return MSAL_RESPONSE_PARAM.test(hash.replace(/^#\/?/, ''))
}

/**
 * Handle an MSAL auth-redirect landing. Returns true when this document first
 * loaded with an MSAL response fragment — in which case it also restores that raw
 * fragment (the router may have rewritten it to `#/code=...`) so the opener's
 * MSAL can read the code, and the caller MUST skip app bootstrap. Returns false
 * for a normal load.
 *
 * Keyed on the hash alone, not window.opener: the popup's cross-origin hop to
 * login.microsoftonline.com can null out window.opener, so it is not reliable. A
 * non-route MSAL fragment is itself unambiguous — the app never routes to one.
 */
export function handleMsalRedirectLanding(): boolean {
  if (!isMsalRedirectHash(initialHash)) return false
  if (typeof window !== 'undefined' && window.location.hash !== initialHash) {
    // The restore is synchronous, so the opener never observes the rewritten value.
    history.replaceState(history.state, '', initialHash)
  }
  return true
}
